import React, { forwardRef, useEffect, useState } from 'react';
import styles from './pdfPreview.module.css';
import FileUtils from '../../utils/file.utils';

export enum PdfScrollMode {
  VERTICAL = 0,
  HORIZONTAL = 1,
  WRAPPED = 2,
  PAGE = 3,
}

export enum PdfSpreadMode {
  UNKNOWN = -1,
  NONE = 0,
  ODD = 1,
  EVEN = 2,
}

interface PdfPreviewProps {
  className?: string;
  pdfFile: File | null | undefined,
  style?: React.CSSProperties,
  pageWidth?: number,
  pageHeight?: number,
  zoom?: number,
  scrollMode?: PdfScrollMode,
  spreadMode?: PdfSpreadMode,
}

const PdfPreview = forwardRef<HTMLIFrameElement, PdfPreviewProps>(({
  className, pdfFile, zoom = 1, pageHeight, pageWidth, style, scrollMode = PdfScrollMode.VERTICAL, spreadMode = PdfSpreadMode.NONE,
}, ref) => {
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    if (pdfFile) {
      const url = URL.createObjectURL(pdfFile);
      FileUtils.validateDocumentFile(pdfFile, true).then((pdfFileError) => setDataUrl(pdfFileError ? null : url));

      return () => {
        URL.revokeObjectURL(url);
      };
    }
    setDataUrl(null);
    return undefined;
  }, [pdfFile]);

  useEffect(() => () => {
    if (dataUrl) {
      URL.revokeObjectURL(dataUrl);
    }
  }, [dataUrl]);

  const containerStyle: React.CSSProperties = {
    width: pageWidth ? `${pageWidth}px` : '100%',
    height: pageHeight ? `${pageHeight}px` : '100%',
    position: 'relative',
    overflow: 'hidden',
  };

  const iframeStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: 'none',
    transform: `scale(${zoom})`,
    transformOrigin: 'top left',
    ...style,
  };

  useEffect(() => {
    localStorage.removeItem('pdfjs.history');

    return () => {
      localStorage.removeItem('pdfjs.history');
    };
  }, []);

  return (
    <div className={[styles.container, className].join(' ')} style={containerStyle}>
      {dataUrl && (
      <iframe
        ref={ref}
        src={`pdfjs/web/viewer.html?file=${dataUrl}&ccMode=preview&ccScrollMode=${scrollMode}&ccSpreadMode=${spreadMode}`}
        style={iframeStyle}
        title="pdf-preview"
      />
      )}
    </div>
  );
});

PdfPreview.displayName = 'PdfPreview';

export default PdfPreview;
